/**
 * Browser Panel Component
 *
 * Displays the embedded sandbox browser via screenshot-based control (Kernel style).
 * Used in split view layout alongside the chat panel.
 *
 * Supports two modes:
 * 1. Screenshot mode (default) - displays PNG screenshots, handles clicks via coordinates
 * 2. noVNC mode (fallback) - iframe streaming for VNC-based display
 */

import { html, nothing } from "lit";
import { icons } from "../icons.js";

export type BrowserPanelMode = "screenshot" | "novnc" | "liveview";

export type BrowserPanelProps = {
  /** Display mode: screenshot (API control), novnc (VNC streaming), liveview (Kernel interactive) */
  mode?: BrowserPanelMode;
  /** Base64 screenshot data (for screenshot mode) */
  screenshot: string | null;
  /** Current browser URL */
  currentUrl: string | null;
  /** Whether browser is loading */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** noVNC WebSocket URL (for novnc mode fallback) */
  noVncUrl: string | null;
  /** Kernel live view URL (for liveview mode - interactive iframe) */
  liveViewUrl: string | null;
  /** Whether the connection is established */
  connected: boolean;
  /** Whether the browser panel is visible */
  visible: boolean;
  /** Whether fullscreen mode is active */
  fullscreen: boolean;
  /** Callback when connection status changes */
  onConnectionChange?: (connected: boolean) => void;
  /** Callback to toggle fullscreen */
  onToggleFullscreen?: () => void;
  /** Callback to toggle panel visibility */
  onToggleVisible?: () => void;
  /** Callback for screenshot click (x, y coordinates) */
  onClick?: (x: number, y: number) => void;
  /** Callback for navigation */
  onNavigate?: (url: string) => void;
  /** Callback for typing */
  onType?: (text: string) => void;
  /** Callback for key press */
  onKey?: (key: string) => void;
  /** Callback to refresh screenshot */
  onRefresh?: () => void;
};

/**
 * Build the noVNC iframe URL from WebSocket URL.
 * Converts ws:// to http:// and wss:// to https:// for the noVNC web client.
 */
function buildNoVncIframeUrl(wsUrl: string): string {
  const httpUrl = wsUrl.replace(/^ws:/, "http:").replace(/^wss:/, "https:");
  return `${httpUrl}/vnc.html?autoconnect=true&resize=scale&reconnect=true&reconnect_delay=1000`;
}

/**
 * Handle click on screenshot image.
 * Calculates actual coordinates based on image scaling.
 */
function handleScreenshotClick(event: MouseEvent, onClick?: (x: number, y: number) => void) {
  if (!onClick) {
    return;
  }

  const img = event.target as HTMLImageElement;
  const rect = img.getBoundingClientRect();

  // Calculate click position relative to image
  const clickX = event.clientX - rect.left;
  const clickY = event.clientY - rect.top;

  // Scale to actual browser dimensions (assuming 1920x1080 viewport)
  const scaleX = img.naturalWidth / rect.width;
  const scaleY = img.naturalHeight / rect.height;

  const x = Math.round(clickX * scaleX);
  const y = Math.round(clickY * scaleY);

  onClick(x, y);
}

/**
 * Handle URL bar submit for navigation.
 */
function handleUrlSubmit(event: Event, onNavigate?: (url: string) => void) {
  event.preventDefault();
  if (!onNavigate) {
    return;
  }

  const form = event.target as HTMLFormElement;
  const input = form.querySelector('input[name="url"]') as HTMLInputElement;
  if (input?.value.trim()) {
    let url = input.value.trim();
    // Add protocol if missing
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "https://" + url;
    }
    onNavigate(url);
  }
}

export function renderBrowserPanel(props: BrowserPanelProps) {
  if (!props.visible) {
    return nothing;
  }

  const mode = props.mode ?? "screenshot";

  // Screenshot mode (Kernel style)
  if (mode === "screenshot") {
    return html`
      <div class="browser-panel ${props.fullscreen ? "browser-panel--fullscreen" : ""}">
        <div class="browser-panel__toolbar">
          <form
            class="browser-panel__url-bar"
            @submit=${(e: Event) => handleUrlSubmit(e, props.onNavigate)}
          >
            <input
              type="text"
              name="url"
              class="browser-panel__url-input"
              placeholder="Enter URL..."
              .value=${props.currentUrl ?? ""}
              ?disabled=${props.loading}
            />
          </form>
          <div class="browser-panel__status">
            <span class="statusDot ${props.connected ? "ok" : ""}"></span>
            <span class="browser-panel__status-text">
              ${props.loading ? "Working..." : props.connected ? "Live browser" : "Connecting..."}
            </span>
          </div>
          <div class="browser-panel__actions">
            <button
              class="browser-panel__btn"
              @click=${props.onToggleFullscreen}
              title="${props.fullscreen ? "Exit fullscreen" : "Fullscreen"}"
            >
              ${props.fullscreen ? icons.minimize : icons.maximize}
            </button>
            <button
              class="browser-panel__btn"
              @click=${props.onToggleVisible}
              title="Close browser panel"
            >
              ${icons.x}
            </button>
          </div>
        </div>
        <div class="browser-panel__content">
          ${
            props.error
              ? html`<div class="browser-panel__error">${props.error}</div>`
              : props.screenshot
                ? html`
                  <img
                    class="browser-panel__screenshot ${props.loading ? "browser-panel__screenshot--loading" : ""}"
                    src="data:image/png;base64,${props.screenshot}"
                    @click=${(e: MouseEvent) => handleScreenshotClick(e, props.onClick)}
                    alt="Browser screenshot"
                  />
                `
                : html`
                  <div class="browser-panel__placeholder">
                    <div class="browser-panel__placeholder-icon">${icons.monitor}</div>
                    <p>Enter a URL above to start browsing</p>
                    ${
                      props.onRefresh
                        ? html`<button class="browser-panel__start-btn" @click=${props.onRefresh}>
                          Take Screenshot
                        </button>`
                        : nothing
                    }
                  </div>
                `
          }
        </div>
      </div>
    `;
  }

  // Kernel Live View mode (interactive iframe)
  if (mode === "liveview" && props.liveViewUrl) {
    return html`
      <div class="browser-panel ${props.fullscreen ? "browser-panel--fullscreen" : ""}">
        <div class="browser-panel__toolbar">
          <div class="browser-panel__status">
            <span class="statusDot ${props.connected ? "ok" : ""}"></span>
            <span class="browser-panel__status-text">
              ${props.connected ? "Kernel Browser" : "Connecting to Kernel..."}
            </span>
          </div>
          <div class="browser-panel__actions">
            <button
              class="browser-panel__btn"
              @click=${props.onToggleFullscreen}
              title="${props.fullscreen ? "Exit fullscreen" : "Fullscreen"}"
            >
              ${props.fullscreen ? icons.minimize : icons.maximize}
            </button>
            <button
              class="browser-panel__btn"
              @click=${props.onToggleVisible}
              title="Close browser panel"
            >
              ${icons.x}
            </button>
          </div>
        </div>
        <div class="browser-panel__content">
          <iframe
            class="browser-panel__iframe browser-panel__iframe--liveview"
            src="${props.liveViewUrl}"
            @load=${() => props.onConnectionChange?.(true)}
            @error=${() => props.onConnectionChange?.(false)}
            allow="clipboard-read; clipboard-write; microphone; camera"
          ></iframe>
        </div>
      </div>
    `;
  }

  // noVNC mode (fallback)
  if (!props.noVncUrl) {
    return nothing;
  }

  const iframeUrl = buildNoVncIframeUrl(props.noVncUrl);

  return html`
    <div class="browser-panel ${props.fullscreen ? "browser-panel--fullscreen" : ""}">
      <div class="browser-panel__toolbar">
        <div class="browser-panel__status">
          <span class="statusDot ${props.connected ? "ok" : ""}"></span>
          <span class="browser-panel__status-text">
            ${props.connected ? "Connected" : "Connecting..."}
          </span>
        </div>
        <div class="browser-panel__actions">
          <button
            class="browser-panel__btn"
            @click=${props.onToggleFullscreen}
            title="${props.fullscreen ? "Exit fullscreen" : "Fullscreen"}"
          >
            ${props.fullscreen ? icons.minimize : icons.maximize}
          </button>
          <button
            class="browser-panel__btn"
            @click=${props.onToggleVisible}
            title="Close browser panel"
          >
            ${icons.x}
          </button>
        </div>
      </div>
      <div class="browser-panel__content">
        <iframe
          class="browser-panel__iframe"
          src="${iframeUrl}"
          @load=${() => props.onConnectionChange?.(true)}
          @error=${() => props.onConnectionChange?.(false)}
          allow="clipboard-read; clipboard-write"
        ></iframe>
      </div>
    </div>
  `;
}

/**
 * Renders a toggle button to show/hide the browser panel.
 */
export function renderBrowserToggle(props: {
  visible: boolean;
  available: boolean;
  onToggle: () => void;
}) {
  if (!props.available) {
    return nothing;
  }

  return html`
    <button
      class="browser-toggle ${props.visible ? "browser-toggle--active" : ""}"
      @click=${props.onToggle}
      title="${props.visible ? "Hide browser" : "Show browser"}"
    >
      ${icons.monitor}
      <span class="browser-toggle__label">Browser</span>
    </button>
  `;
}
